window.VOGRACE = window.VOGRACE || {};

window.VOGRACE.popupMap = new Map();

/**
 * @description 沙盒模式
 * @param {(instance: VograceInstance, window: Window, document: Document) => void} callback 回调函数
 */
window.VOGRACE.sandbox = function sandbox(callback) {
  try {
    this.isFunction(callback) && callback(this, window, document);
  } catch (error) {
    this.logger.error(error);
  }
};

/**
 * @description 获取元素
 * @param {String} selector 选择器
 * @param {HTMLElement} [context=document] 上下文
 * @returns {HTMLElement} 元素
 */
window.VOGRACE.$ = function $(selector, context = document) {
  return context.querySelector(selector);
};

/**
 * @description 获取元素列表
 * @param {String} selector 选择器
 * @param {HTMLElement} [context=document] 上下文
 * @returns {NodeListOf<Element>} 元素列表
 */
window.VOGRACE.$$ = function $$(selector, context = document) {
  return context.querySelectorAll(selector);
};

/**
 * @description 获取元素的兄弟元素
 * @param {Element} element 当前元素
 * @param {string} selector 选择器
 * @returns {Element|null} 兄弟元素
 */
window.VOGRACE.getSiblingElement = function getSiblingElement(
  element,
  selector
) {
  let sibling = element.parentNode.firstChild;

  while (sibling) {
    // 只匹配指定类型为 ELEMENT_NODE 的节点（跳过文本节点等）
    if (sibling.nodeType === 1 && sibling !== element) {
      // 使用 matches 方法判断是否符合传入的选择器
      if (sibling.matches(selector)) {
        return sibling;
      }
    }
    sibling = sibling.nextSibling;
  }

  return null;
};

/**
 * @description 创建元素
 * @param {string} tagName 标签名
 * @param {Record<string, string>} [attributes={}] 属性
 * @param {Array<HTMLElement>|Element|string} [children=[]] 子元素
 * @returns {HTMLElement} 元素
 */
window.VOGRACE.createElement = function createElement(
  tagName,
  attributes = {},
  children = []
) {
  const element = document.createElement(tagName);

  if (attributes) {
    Object.keys(attributes).forEach((key) => {
      element.setAttribute(key, attributes[key]);
    });
  }

  if (this.isArray(children)) {
    children.forEach((child) => {
      element.appendChild(child);
    });
  } else {
    element.innerHTML = children;
  }

  return element;
};

/**
 * @description 为指定的选择器绑定点击事件监听器
 * @param {string|Element|Document|Window} selector - 选择器或元素
 * @param {string} event - 事件类型
 * @param {(e: Event) => void} handler - 点击事件触发时执行的回调函数
 */
window.VOGRACE.on = function on(selector, event, handler) {
  // 如果 selector 是字符串，则使用 $ 函数查询元素
  if (this.isString(selector)) {
    const element = this.$(selector);
    if (element) {
      element.addEventListener(event, handler);
    } else {
      this.logger.warn(`Element with selector "${selector}" not found.`);
    }
  } else {
    selector.addEventListener(event, handler);
  }
};

/**
 * @description 为指定的选择器移除点击事件监听器
 * @param {string|Element|Document|Window} selector - 选择器或元素
 * @param {string} event - 事件类型
 * @param {(e: Event) => void} handler - 点击事件触发时执行的回调函数
 */
window.VOGRACE.off = function off(selector, event, handler) {
  if (this.isString(selector)) {
    const element = this.$(selector);
    if (element) {
      element.removeEventListener(event, handler);
    } else {
      this.logger.warn(`Element with selector "${selector}" not found.`);
    }
  } else {
    selector.removeEventListener(event, handler);
  }
};

/**
 * @description 等待文档加载完成后执行回调函数
 * @param {Function} callback 回调函数
 */
window.VOGRACE.ready = function ready(callback) {
  // 如果文档状态为 `loading`，表示 DOM 正在加载
  if (document.readyState === "loading") {
    // 添加事件监听器，当 DOM 加载完成时触发 `DOMContentLoaded` 事件，然后执行回调函数 callback
    document.addEventListener("DOMContentLoaded", callback);
  } else {
    // 如果文档状态不是 `loading`，说明 DOM 已经加载完成
    // 防止文档已加载完成但事件已被触发的情况
    // 直接执行回调函数 callback，确保期望的回调行为得以执行
    callback();
  }
};

/**
 * @description 判断是否是移动端（基于媒体查询，可自定义断点）
 * @param {number} [breakpoint=767] 断点值（像素）
 * @returns {boolean} 是否是移动端
 */
window.VOGRACE.isMobile = function isMobile(breakpoint = 767) {
  return window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
};

/**
 * @description 空函数
 */
window.VOGRACE.noop = function noop() {};

/**
 * @description 判断是否是 undefined
 * @param {unknown} val 值
 * @returns {boolean} 是否是 undefined
 */
window.VOGRACE.isUndefined = function isUndefined(val) {
  return val === void 0;
};

/**
 * @description 判断是否是字符串
 * @param {unknown} val 值
 * @returns {boolean} 是否是字符串
 */
window.VOGRACE.isString = function isString(val) {
  return typeof val === "string";
};

/**
 * @description 判断是否是数字
 * @param {Number} val
 * @return {Boolean}
 */
window.VOGRACE.isNumber = function isNumber(val) {
  return typeof val === "number";
};

/**
 * @description 判断是否是布尔值
 * @param {Boolean} val
 * @return {Boolean}
 */
window.VOGRACE.isBoolean = function isBoolean(val) {
  return typeof val === "boolean";
};

/**
 * @description 判断是否是 null
 * @param {unknown} val 值
 * @returns {boolean} 是否是 null
 */
window.VOGRACE.isNull = function isNull(val) {
  return val === null;
};

/**
 * @description 判断是否是数组
 * @param {unknown} val 值
 * @returns {boolean} 是否是数组
 */
window.VOGRACE.isArray = function isArray(val) {
  return Array.isArray(val);
};

/**
 * @description 判断是否是对象
 * @param {unknown} val 值
 * @returns {boolean} 是否是对象
 */
window.VOGRACE.isObject = function isObject(val) {
  return !this.isNull(val) && typeof val === "object";
};

/**
 * @description 判断是否是对象
 * @param {unknown} val 值
 * @returns {boolean} 是否是函数
 */
window.VOGRACE.isFunction = function isFunction(val) {
  return typeof val === "function";
};

/**
 * @description 等待指定时间
 * @param {number} ms - 等待的时间，以毫秒为单位
 */
window.VOGRACE.wait = function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * @description 应用 CSS 样式
 * @param {Element} element - 要应用样式的元素
 * @param {string | CSSProperties} styles - 要应用的 CSS 样式
 */
window.VOGRACE.css = function css(element, styles) {
  // 检查元素是否存在
  if (!element || !element.style) {
    this.logger.warn("Invalid element provided to css function");
    return;
  }

  // 如果样式是字符串类型
  if (this.isString(styles)) {
    // 直接设置CSS文本
    element.style.cssText = styles;
    return;
  }

  // 如果样式是对象类型
  if (this.isObject(styles)) {
    // 遍历对象中的每个样式属性
    Object.keys(styles).forEach((property) => {
      let value = styles[property];
      value = this.isNumber(value) ? value + "px" : value;

      // 处理样式属性名，将驼峰命名转换为短横线命名
      const kebabCaseProperty = property.replace(
        /([A-Z])/g,
        (match) => `-${match.toLowerCase()}`
      );

      // 设置样式
      element.style.setProperty(kebabCaseProperty, value);
    });
    return;
  }

  this.logger.warn(
    "Invalid styles provided to css function. Expected string or object."
  );
};

/**
 * @description 获取元素在页面中的位置信息
 * @param {HTMLElement} element
 * @returns {DOMRect}
 */
window.VOGRACE.getRect = function getRect(element) {
  return element.getBoundingClientRect();
};

/**
 * @description 获取页面滚动位置
 * @returns {number}
 */
window.VOGRACE.getScrollTop = function getScrollTop() {
  return window.pageYOffset || document.documentElement.scrollTop;
};

/**
 * @description 防抖函数
 * @param {Function} func 要执行的函数
 * @param {number} delay 延迟时间（毫秒）
 * @returns {Function} 包裹后的防抖函数
 */
window.VOGRACE.debounce = function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
};

/**
 * @description 节流函数
 * @param {Function} func 要执行的函数
 * @param {number} delay 执行间隔时间（毫秒）
 * @returns {Function} 包裹后的节流函数
 */
window.VOGRACE.throttle = function throttle(func, delay) {
  let lastCall = 0;
  return (...args) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      func.apply(this, args);
    }
  };
};

/**
 * @description 显示加载动画
 * @param {Element} target 显示加载动画的目标元素
 * @returns {Function}
 */
window.VOGRACE.showLoading = function showLoading(target) {
  target.innerHTML = `
    <div class="vograce-loading">
      <span class="vograce-loading__spinner" role="img" aria-label="loading">
        <svg
          viewBox="0 0 1024 1024"
          focusable="false"
          width="1em"
          height="1em"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M988 548c-19.9 0-36-16.1-36-36 0-59.4-11.6-117-34.6-171.3a440.45 440.45 0 00-94.3-139.9 437.71 437.71 0 00-139.9-94.3C629 83.6 571.4 72 512 72c-19.9 0-36-16.1-36-36s16.1-36 36-36c69.1 0 136.2 13.5 199.3 40.3C772.3 66 827 103 874 150c47 47 83.9 101.8 109.7 162.7 26.7 63.1 40.2 130.2 40.2 199.3.1 19.9-16 36-35.9 36z"></path>
        </svg>
      </span>
    </div>
  `;
  return () => {
    target.innerHTML = "";
  };
};

/**
 * @description 显示登录弹窗
 */
window.VOGRACE.showLoginPopup = function showLoginPopup() {
  const popup = this.popupMap.get("#AuthDialog");
  popup.open();
  this.eventBus.emit("auth:login");
};

/**
 * @description 显示注册弹窗
 */
window.VOGRACE.showRegisterPopup = function showRegisterPopup() {
  const popup = this.popupMap.get("#AuthDialog");
  popup.open();
  this.eventBus.emit("auth:register");
};

/**
 * @description 日志类
 */
window.VOGRACE.logger = window.VOGRACE.logger || {};

/**
 * @description 日志打印
 * @param  {...any} args 参数
 */
window.VOGRACE.logger.info = function info(...args) {
  console.info("INFO:", ...args);
};

/**
 * @description 错误日志打印
 * @param  {...any} args 参数
 */
window.VOGRACE.logger.error = function error(...args) {
  console.error("ERROR:", ...args);
};

/**
 * @description 警告日志打印
 * @param  {...any} args 参数
 */
window.VOGRACE.logger.warn = function warn(...args) {
  console.warn("WARN:", ...args);
};

/**
 * @description 调试日志打印
 * @param  {...any} args 参数
 */
window.VOGRACE.logger.debug = function debug(...args) {
  console.debug("DEBUG:", ...args);
};

/**
 * ======================================
 * ======= Page State Manager
 * ======================================
 */
window.VOGRACE.sandbox((_) => {
  class PageStateManager {
    constructor() {
      this.state = new Map();

      this._init();
    }

    _init() {
      _.on(window, "popstate", this._handlePopState.bind(this));
    }

    /**
     * @param {PopStateEvent} event
     */
    _handlePopState(event) {
      if (!event.state?.pageKey || !this.state.has(event.state.pageKey)) {
        // win.location.reload();
      }
    }

    /**
     * @param {string} key
     */
    save(key) {
      this.state.set(key, {
        html: document.documentElement.outerHTML,
        timestamp: Date.now(),
      });
      window.history.pushState({ pageKey: key }, "", key);
    }
  }

  _.pageStateManager = new PageStateManager();
});

/**
 * ======================================
 * ======= Fetch
 * ======================================
 */
window.VOGRACE.sandbox((_, win, doc) => {
  class InterceptorManager {
    constructor() {
      this.handlers = [];
    }

    use(fulfilled, rejected) {
      this.handlers.push({ fulfilled, rejected });
      return this.handlers.length - 1;
    }

    eject(id) {
      if (this.handlers[id]) {
        this.handlers[id] = null;
      }
    }

    forEach(fn) {
      this.handlers.forEach((h) => {
        if (h !== null) {
          fn(h);
        }
      });
    }
  }

  class Fetch {
    constructor() {
      this.defaults = {
        baseUrl: "",
        headers: {
          "Content-Type": "application/json",
        },
        timeout: 10000,
      };
      this.interceptors = {
        request: new InterceptorManager(),
        response: new InterceptorManager(),
      };
      this.requestMap = new Map();
    }

    async request(config) {
      const fullConfig = this._mergeConfig(config);
      const key = `${fullConfig.method}:${fullConfig.url}`; // 生成请求唯一标识符

      if (this.requestMap.has(key)) {
        _.logger.warn(`[${key}] 请求正在处理中...`);
        return this.requestMap.get(key);
      }

      // 触发请求拦截器
      let promise = Promise.resolve(fullConfig);
      const chain = [];

      this.interceptors.request.forEach((interceptor) => {
        chain.unshift(interceptor.fulfilled, interceptor.rejected);
      });

      chain.push(this._fetchAdapter.bind(this), undefined); // 使用 fetch 作为适配器

      while (chain.length) {
        const onFulfilled = chain.shift();
        const onRejected = chain.shift();
        promise = promise.then(onFulfilled, onRejected);
      }

      // 存储当前请求的 Promise
      this.requestMap.set(key, promise);

      // 请求完成后从 map 中移除
      promise = promise.finally(() => {
        this.requestMap.delete(key);
      });

      const response = await promise;

      // 触发响应拦截器
      let responsePromise = Promise.resolve(response);
      this.interceptors.response.forEach((interceptor) => {
        responsePromise = responsePromise.then(
          interceptor.fulfilled,
          interceptor.rejected
        );
      });

      return responsePromise;
    }

    _mergeConfig(config) {
      return {
        ...this.defaults,
        ...config,
        headers: {
          ...this.defaults.headers,
          ...(config.headers || {}),
        },
      };
    }

    async _fetchAdapter(config) {
      const controller = new AbortController();
      const signal = controller.signal;

      const timeoutId = setTimeout(() => {
        controller.abort();
      }, config.timeout);

      let body;
      if (["post", "put"].includes(config.method)) {
        if (
          config.headers &&
          config.headers["Content-Type"] === "application/x-www-form-urlencoded"
        ) {
          body = config.data; // 已经是 URLSearchParams.toString() 的结果
        } else {
          body = JSON.stringify(config.data);
        }
      }

      try {
        const response = await fetch(config.baseUrl + config.url, {
          method: config.method,
          headers: config.headers,
          body,
          signal,
        });
        clearTimeout(timeoutId);
        // 检查响应有效性
        if (!response.ok) {
          const contentType = response.headers.get("Content-Type");

          // 如果响应体已被消费，直接返回错误信息
          if (response.bodyUsed) {
            throw {
              message: "Response body already consumed",
              status: response.status,
              response: response,
            };
          }

          // 创建响应副本用于读取
          const responseClone = response.clone();

          // 根据内容类型选择解析方式
          let errorData;
          if (contentType.includes("application/json")) {
            errorData = await responseClone.json();
          } else {
            errorData = await responseClone.text();
            if (responseClone.status >= 400) {
              const html = await _.fetch.html(errorData);
              _.pageStateManager.save(response.url);
              document.documentElement.innerHTML =
                html.documentElement.innerHTML;
            }
          }

          throw {
            status: response.status,
            data: errorData,
            response: response,
          };
        } else {
          return response;
        }
      } catch (error) {
        clearTimeout(timeoutId);
        throw error;
      }
    }

    get(url, config = {}) {
      return this.request({ ...config, method: "get", url });
    }

    getJSON(url, config = {}) {
      config.baseUrl = config.baseUrl || getBaseUrl();
      return this.request({ ...config, method: "get", url });
    }

    post(url, data = {}, config = {}) {
      return this.request({ ...config, method: "post", url, data });
    }

    postJSON(url, data = {}, config = {}) {
      config.baseUrl = config.baseUrl || getBaseUrl();
      return this.request({ ...config, method: "post", url, data });
    }

    delete(url, config) {
      return this.request({ ...config, method: "delete", url });
    }

    /**
     * @param {Response | string} response
     */
    async html(response) {
      let html;
      if (_.isString(response)) {
        html = response;
      } else {
        html = await response.text();
      }
      const parser = new DOMParser();
      return parser.parseFromString(html, "text/html");
    }
  }

  _.fetch = new Fetch();

  _.fetch.interceptors.request.use(function (config) {
    config.headers = {
      ...config.headers,
      "customer-id": customer_id || localStorage.getItem("chat_token"),
      sign: customer_slat,
      timestamp: now_timestamp,
      "shop-domain": getShopifyDomain(config.baseUrl),
      "X-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    return config;
  });

  _.fetch.interceptors.response.use(function (response) {
    return response;
  });

  function getBaseUrl() {
    switch (window.location.hostname) {
      case "xn-ooru7az5q7qa810dn7x8fucnce02b.myshopify.com":
      case "localhost":
      case "127.0.0.1":
      case "xn-ooru7ap8r6la930dn7x8fucnc.myshopify.com":
        return "https://vograce-app-testing.baigedi.com/shopify-api";

      case "vograce.com":
      default:
        return "https://app.vograce.com/shopify-api";
    }
  }

  function getShopifyDomain(baseUrl) {
    baseUrl = baseUrl || getBaseUrl();

    switch (baseUrl) {
      case "https://vograce-app-testing.baigedi.com/shopify-api":
        return "vograce-dev";

      case "https://app.vograce.com/shopify-api":
      default:
        return "vograce";
    }
  }
});

/**
 * ======================================
 * ======= Event Bus
 * ======================================
 */
window.VOGRACE.sandbox((_) => {
  class EventBus {
    constructor() {
      this.events = {};
    }

    // 辅助方法：确保事件名存在且是一个数组
    _ensureEventArray(eventName) {
      if (!_.isString(eventName) || eventName.trim() === "") {
        throw new Error("Event name must be a non-empty string.");
      }
      if (!this.events[eventName]) {
        this.events[eventName] = [];
      }
      return this.events[eventName];
    }

    on(eventName, callback) {
      if (!_.isFunction(callback)) {
        throw new TypeError("Callback must be a function.");
      }
      this._ensureEventArray(eventName).push(callback);
    }

    off(eventName, callback) {
      const callbacks = this.events[eventName];
      if (!callbacks) return;

      let index;
      while ((index = callbacks.indexOf(callback)) !== -1) {
        callbacks.splice(index, 1);
      }

      if (callbacks.length === 0) {
        delete this.events[eventName];
      }
    }

    emit(eventName, ...args) {
      const callbacks = this.events[eventName];
      if (!callbacks) return;

      // 避免在执行期间修改原数组造成的问题
      callbacks.slice().forEach((callback) => {
        try {
          callback(...args);
        } catch (error) {
          this.logger.error(
            `Error occurred in event handler for ${eventName}:`,
            error
          );
        }
      });
    }

    once(eventName, callback) {
      if (!_.isFunction(callback)) {
        throw new TypeError("Callback must be a function.");
      }

      const onceCallback = (...args) => {
        try {
          callback(...args);
        } finally {
          this.off(eventName, onceCallback);
        }
      };

      this.on(eventName, onceCallback);
      return onceCallback;
    }

    clear() {
      this.events = {};
    }
  }

  _.eventBus = new EventBus();
});

/**
 * ======================================
 * ======= Collapse
 * ======================================
 */
window.VOGRACE.sandbox((_) => {
  const selectors = {
    ROOT: "[data-root='collapse']",
    TRIGGER: "[data-trigger='collapse']",
    ICON_CLOSED: "[data-icon='plus']",
    ICON_OPENED: "[data-icon='minus']",
  };

  const collapseTypes = {
    ACCORDION: "accordion",
  };

  class CollapseItemController {
    constructor(trigger, collapseType, eventName) {
      /** @type {Element} */
      this.$trigger = trigger;
      /** @type {Element} */
      this.$triggerClosedIcon = _.$(selectors.ICON_CLOSED, this.$trigger);
      /** @type {Element} */
      this.$triggerOpenedIcon = _.$(selectors.ICON_OPENED, this.$trigger);
      /** @type {Element} */
      this.$target = _.$(this.$trigger.dataset.target);

      this.collapseType = collapseType;
      this.eventName = eventName;

      this._init();
    }

    _init() {
      this._initOpened();
      this._bindEvents();
    }

    _bindEvents() {
      _.on(this.$trigger, "click", this._toggle.bind(this));
    }

    _initOpened() {
      this._opened = this.$target.dataset.visibility === "visible";
      if (this._opened) {
        this.$triggerClosedIcon.setAttribute("data-visibility", "hidden");
        this.$triggerOpenedIcon.setAttribute("data-visibility", "visible");
      }
    }

    _toggle() {
      if (!this._opened) {
        if (this.collapseType === collapseTypes.ACCORDION) {
          _.eventBus.emit(this.eventName);
        }
        this._open();
      } else {
        this.close();
      }
    }

    _open() {
      this._opened = true;
      this.$target.setAttribute("data-visibility", "visible");
      this.$triggerClosedIcon.setAttribute("data-visibility", "hidden");
      this.$triggerOpenedIcon.setAttribute("data-visibility", "visible");
    }

    close() {
      this._opened = false;
      this.$target.setAttribute("data-visibility", "hidden");
      this.$triggerOpenedIcon.setAttribute("data-visibility", "hidden");
      this.$triggerClosedIcon.setAttribute("data-visibility", "visible");
    }
  }

  class CollapseController {
    constructor(root) {
      /** @type {Element} */
      this.$root = root;
      /** @type {CollapseItemController[]} */
      this.controllers = [];

      this._init();
    }

    _init() {
      const collapseType = this.$root.dataset.type;
      const eventName = `collapse:${this.$root.dataset.name}`;

      _.$$(selectors.TRIGGER, this.$root).forEach(($trigger) => {
        const controller = new CollapseItemController(
          $trigger,
          collapseType,
          eventName
        );
        this.controllers.push(controller);
      });

      if (collapseType === collapseTypes.ACCORDION) {
        _.eventBus.on(eventName, this._onUpdate.bind(this));
      }
    }

    _onUpdate() {
      this.controllers.forEach((controller) => {
        controller.close();
      });
    }
  }

  _.ready(() => {
    _.$$(selectors.ROOT).forEach((el) => {
      new CollapseController(el);
    });
  });
});

/**
 * ======================================
 * ======= Popup
 * ======================================
 */
window.VOGRACE.sandbox((_) => {
  const selectors = {
    TRIGGER: "[data-trigger='popup']",
    DISMISS: "[data-dismiss='popup']",
    CONTENT: ".vograce-popup__content",
  };

  class PopupController {
    /**
     * @constructor
     * @param {Element} $trigger 触发器元素
     * @param {string} id 对应 Popup 元素 ID
     *
     */
    constructor($trigger, id) {
      this.id = id;
      this.zIndex = 5000 + _.popupMap.size;
      /** @type {Element} */
      this.$popup = _.$(id);
      /** @type {Element} */
      this.$trigger = $trigger;
      /** @type {Element} */
      this.$content = _.$(selectors.CONTENT, this.$popup);
      this._init();
    }

    _init() {
      this._setZIndex();
      this._bindEvents();
    }

    _setZIndex() {
      this.$popup.setAttribute("style", `z-index: ${this.zIndex}`);
    }

    _bindEvents() {
      _.on(this.$trigger, "click", this.open.bind(this));
      _.$$(selectors.DISMISS, this.$popup).forEach((el) => {
        _.on(el, "click", this.close.bind(this));
      });
    }

    open() {
      this.$popup.setAttribute("data-visibility", "visible");
      document.body.setAttribute("style", "overflow: hidden");
      _.eventBus.emit("popup:open", this.id, this.$popup, this);
    }

    close() {
      this.$popup.setAttribute("data-visibility", "hidden");
      document.body.setAttribute("style", "overflow: auto");
      _.eventBus.emit("popup:close", this.id, this.$popup);
    }
  }

  _.ready(() => {
    _.$$('[data-trigger="popup"]').forEach((el) => {
      const target = el.dataset.target;
      _.popupMap.set(target, new PopupController(el, target));
    });
  });
});

/**
 * ======================================
 * ======= Auth Dialog
 * ======================================
 */
window.VOGRACE.sandbox((_) => {
  const selectors = Object.freeze({
    signIn: "#SignIn",
    signInEmail: "#SignInEmail",
    signInPwd: "#SignInPassword",
    signUp: "#SignUp",
    signUpFirstName: "#SignUpFirstName",
    signUpLastName: "#SignUpLastName",
    signUpEmail: "#SignUpEmail",
    signUpPwd: "#SignUpPassword",
    resetPwd: "#ResetPassword",
    resetPwdEmail: "#ResetPasswordEmail",
    submitButton: ".vograce-auth-dialog__submit-button",
    submitLoading: "[data-icon='loading']",
    switchButton: ".vograce-auth-dialog__switch-button",
    forgotPwdButton: ".vograce-auth-dialog__forgot-password-button",
    errors: ".vograce-auth-dialog__errors",
  });

  class AuthController {
    constructor() {
      /** @type {Element} */
      this.$signIn = _.$(selectors.signIn);
      /** @type {Element} */
      this.$signInForgotPwd = _.$(selectors.forgotPwdButton, this.$signIn);
      /** @type {Element} */
      this.$signInSubmit = _.$(selectors.submitButton, this.$signIn);
      /** @type {Element} */
      this.$signInLoading = _.$(selectors.submitLoading, this.$signIn);
      /** @type {Element} */
      this.$signInSwitch = _.$(selectors.switchButton, this.$signIn);
      /** @type {Element} */
      this.$signInErrors = _.$(selectors.errors, this.$signIn);
      /** @type {Element} */
      this.$signUp = _.$(selectors.signUp);
      /** @type {Element} */
      this.$signUpSubmit = _.$(selectors.submitButton, this.$signUp);
      /** @type {Element} */
      this.$signUpLoading = _.$(selectors.submitLoading, this.$signUp);
      /** @type {Element} */
      this.$signUpErrors = _.$(selectors.errors, this.$signUp);
      /** @type {Element} */
      this.$resetPwd = _.$(selectors.resetPwd);
      /** @type {Element} */
      this.$resetPwdSubmit = _.$(selectors.submitButton, this.$resetPwd);
      /** @type {Element} */
      this.$resetPwdLoading = _.$(selectors.submitLoading, this.$resetPwd);
      /** @type {Element} */
      this.$resetPwdSwitch = _.$(selectors.switchButton, this.$resetPwd);
      /** @type {Element} */
      this.$resetPwdErrors = _.$(selectors.errors, this.$resetPwd);

      this._init();
    }

    _init() {
      this._bindEvents();
    }

    _bindEvents() {
      _.eventBus.on("auth:login", this._toSignIn.bind(this));
      _.eventBus.on("auth:register", this._toSignUp.bind(this));

      _.eventBus.on("popup:open", this._onPopupOpen.bind(this));
      _.eventBus.on("popup:close", this._onPopupClose.bind(this));

      _.on(this.$signInSubmit, "click", this._signIn.bind(this));
      _.on(this.$signUpSubmit, "click", this._signUp.bind(this));
      _.on(this.$resetPwdSubmit, "click", this._resetPwd.bind(this));

      _.on(this.$signInSwitch, "click", this._toSignUp.bind(this));
      _.on(this.$signInForgotPwd, "click", this._toResetPwd.bind(this));
      _.on(this.$resetPwdSwitch, "click", this._toSignUp.bind(this));
    }

    _onPopupOpen(id, popup, controller) {
      this._clearFields();
      this._hideResetSuccess();
      this._hideErrors();

      const $trigger = controller.$trigger;
      const mode = $trigger.getAttribute("data-mode") || "login";
      if (mode === "login") {
        this._toSignIn();
      } else if (mode === "register") {
        this._toSignUp();
      } else {
        this._toResetPwd();
      }
    }

    _onPopupClose(id, popup) {
      this._clearFields();
      this._hideResetSuccess();
      this._hideErrors();
      this._toSignIn();
    }

    _clearFields() {
      /** @type {HTMLInputElement}  */
      const $signInEmail = _.$(selectors.signInEmail);
      /** @type {HTMLInputElement}  */
      const $signInPwd = _.$(selectors.signInPwd);
      /** @type {HTMLInputElement}  */
      const $signUpFirstName = _.$(selectors.signUpFirstName);
      /** @type {HTMLInputElement}  */
      const $signUpLastName = _.$(selectors.signUpLastName);
      /** @type {HTMLInputElement}  */
      const $signUpEmail = _.$(selectors.signUpEmail);
      /** @type {HTMLInputElement}  */
      const $signUpPwd = _.$(selectors.signUpPwd);
      /** @type {HTMLInputElement}  */
      const $resetPwdEmail = _.$(selectors.resetPwdEmail);

      $signInEmail.value = "";
      $signInPwd.value = "";
      $signUpFirstName.value = "";
      $signUpLastName.value = "";
      $signUpEmail.value = "";
      $signUpPwd.value = "";
      $resetPwdEmail.value = "";
    }

    _hideErrors() {
      this.$signInErrors.setAttribute("data-visibility", "hidden");
      this.$signUpErrors.setAttribute("data-visibility", "hidden");
      this.$resetPwdErrors.setAttribute("data-visibility", "hidden");
    }

    _showResetSuccess() {
      this.$signIn.classList.add("is--reset-success");
    }

    _hideResetSuccess() {
      this.$signIn.classList.remove("is--reset-success");
    }

    _toSignIn() {
      this._clearLoading();
      this.$signIn.setAttribute("data-visibility", "visible");
      this.$signUp.setAttribute("data-visibility", "hidden");
      this.$resetPwd.setAttribute("data-visibility", "hidden");
      this._hideErrors();
    }

    _toSignUp() {
      this._clearLoading();
      this.$signUp.setAttribute("data-visibility", "visible");
      this.$signIn.setAttribute("data-visibility", "hidden");
      this.$resetPwd.setAttribute("data-visibility", "hidden");
    }

    _toResetPwd() {
      this._clearLoading();
      this.$resetPwd.setAttribute("data-visibility", "visible");
      this.$signIn.setAttribute("data-visibility", "hidden");
      this.$signUp.setAttribute("data-visibility", "hidden");
    }

    _clearLoading() {
      this.loading = false;
      this.$signInSubmit.classList.remove("is--loading");
      this.$signUpSubmit.classList.remove("is--loading");
      this.$resetPwdSubmit.classList.remove("is--loading");
      this.$signInLoading.setAttribute("data-visibility", "hidden");
      this.$signUpLoading.setAttribute("data-visibility", "hidden");
      this.$resetPwdLoading.setAttribute("data-visibility", "hidden");
    }

    /**
     * @param {Element} btn
     * @param {Element} icon
     */
    _showLoading(btn, icon) {
      this.loading = true;
      btn.classList.add("is--loading");
      icon.setAttribute("data-visibility", "visible");
    }

    /**
     * @param {Element} btn
     * @param {Element} icon
     */
    _hideLoading(btn, icon) {
      this.loading = false;
      btn.classList.remove("is--loading");
      icon.setAttribute("data-visibility", "hidden");
    }

    async _signIn() {
      if (this.loading) return;

      /** @type {HTMLInputElement}  */
      const $email = _.$(selectors.signInEmail);
      /** @type {HTMLInputElement}  */
      const $pwd = _.$(selectors.signInPwd);

      this._showLoading(this.$signInSubmit, this.$signInLoading);

      const formData = new URLSearchParams();
      formData.append("form_type", "customer_login");
      formData.append("utf8", "✓");
      formData.append("customer[email]", $email.value);
      formData.append("customer[password]", $pwd.value);

      const payload = formData.toString();
      try {
        const response = await _.fetch.post("/account/login", payload, {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        });

        this._hideLoading(this.$signInSubmit, this.$signInLoading);

        const doc = await _.fetch.html(response);
        const $form = _.$("#CustomerLoginForm", doc);
        if ($form) {
          const $errors = _.$$(".errors ul li", $form);
          if ($errors && $errors.length > 0) {
            const $list = _.createElement("ul");
            Array.from($errors).forEach((item) => {
              const message = item.textContent.trim();
              const $item = _.createElement("li", null, message);
              $list.appendChild($item);
            });
            this.$signInErrors.innerHTML = "";
            this.$signInErrors.appendChild($list);
            this.$signInErrors.setAttribute("data-visibility", "visible");
            return;
          }
        }

        window.location.reload();
      } catch (error) {
        this._hideLoading(this.$signInSubmit, this.$signInLoading);
      }
    }

    async _signUp() {
      if (this.loading) return;

      /** @type {HTMLInputElement}  */
      const $firstName = _.$(selectors.signUpFirstName);
      /** @type {HTMLInputElement}  */
      const $lastName = _.$(selectors.signUpLastName);
      /** @type {HTMLInputElement}  */
      const $email = _.$(selectors.signUpEmail);
      /** @type {HTMLInputElement}  */
      const $pwd = _.$(selectors.signUpPwd);

      this._showLoading(this.$signUpSubmit, this.$signUpLoading);

      const formData = new URLSearchParams();
      formData.append("form_type", "create_customer");
      formData.append("utf8", "✓");
      formData.append("customer[first_name]", $firstName.value);
      formData.append("customer[last_name]", $lastName.value);
      formData.append("customer[email]", $email.value);
      formData.append("customer[password]", $pwd.value);

      const payload = formData.toString();
      try {
        const response = await _.fetch.post("/account", payload, {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        });

        this._hideLoading(this.$signUpSubmit, this.$signUpLoading);

        const doc = await _.fetch.html(response);
        const $form = _.$("#CreateCustomerForm", doc);
        if ($form) {
          const $errors = _.$$(".errors ul li", $form);
          if ($errors && $errors.length > 0) {
            const $list = _.createElement("ul");
            Array.from($errors).forEach((item) => {
              const message = item.textContent.trim();
              const $item = _.createElement("li", null, message);
              $list.appendChild($item);
            });
            this.$signUpErrors.innerHTML = "";
            this.$signUpErrors.appendChild($list);
            this.$signUpErrors.setAttribute("data-visibility", "visible");
            return;
          }
        }

        window.location.href = response.url;
      } catch (error) {
        this._hideLoading(this.$signUpSubmit, this.$signUpLoading);
      }
    }

    async _resetPwd() {
      if (this.loading) return;

      /** @type {HTMLInputElement}  */
      const $email = _.$(selectors.resetPwdEmail);

      this._showLoading(this.$resetPwdSubmit, this.$resetPwdLoading);

      const formData = new URLSearchParams();
      formData.append("form_type", "recover_customer_password");
      formData.append("utf8", "✓");
      formData.append("customer[email]", $email.value);

      const payload = formData.toString();
      try {
        const response = await _.fetch.post("/account/recover", payload, {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        });

        this._hideLoading(this.$resetPwdSubmit, this.$resetPwdLoading);

        const doc = await _.fetch.html(response);
        const $form = _.$("#RecoverPasswordForm", doc);
        if (!$form.classList.contains("hide")) {
          const $errors = _.$$(".errors ul li", $form);
          if ($errors && $errors.length > 0) {
            const $list = _.createElement("ul");
            Array.from($errors).forEach((item) => {
              const message = item.textContent.trim();
              const $item = _.createElement("li", null, message);
              $list.appendChild($item);
            });
            this.$resetPwdErrors.innerHTML = "";
            this.$resetPwdErrors.appendChild($list);
            this.$resetPwdErrors.setAttribute("data-visibility", "visible");
            return;
          }
        }

        this._showResetSuccess();
        this._toSignIn();
      } catch (error) {
        this._hideLoading(this.$resetPwdSubmit, this.$resetPwdLoading);
      }
    }
  }

  _.ready(() => {
    new AuthController();
  });
});

/**
 * ======================================
 * ======= Country Selector
 * ======================================
 */
window.VOGRACE.sandbox((_) => {
  const rawData = JSON.parse(_.$("#CountryCurrencyData").textContent);
  const countries = rawData.countries;

  const countryToCurrencies = {};
  const currencyToCountries = {};

  // 构建两个方向的映射
  countries.forEach((country) => {
    const countryCode = country.code;
    const currencyCode = country.currency.code;

    // country -> currencies
    if (!countryToCurrencies[countryCode]) {
      countryToCurrencies[countryCode] = [];
    }
    if (!countryToCurrencies[countryCode].includes(currencyCode)) {
      countryToCurrencies[countryCode].push(currencyCode);
    }

    // currency -> countries
    if (!currencyToCountries[currencyCode]) {
      currencyToCountries[currencyCode] = [];
    }
    if (!currencyToCountries[currencyCode].includes(countryCode)) {
      currencyToCountries[currencyCode].push(countryCode);
    }
  });

  const selectors = {
    TRIGGER: "[data-trigger='country-selector']",
    DISMISS: "[data-dismiss='country-selector']",
    CONTENT: ".vograce-country-selector .vograce-popup__content",
    CONTROL: ".vograce-country-selector__control",
    SELECT_TRIGGER: "[data-trigger='select']",
    SELECT_TRIGGER_TEXT: ".text-ellipsis",
    SELECT_TRIGGER_FLAG: ".currency-flag",
    SELECT_DISMISS: "[data-dismiss='select']",
    OPTIONS: ".vograce-country-selector__options",
    OPTION: ".vograce-country-selector__option",
    OPTION_SELECTED: ".vograce-country-selector__option.is--selected",
    INPUT: "input[type='hidden']",
  };

  const store = {
    /**
     * @description Currency Converter Plus Status
     * @type {boolean}
     */
    opened: false,

    /**
     * @type {Map<string, CountrySelectorItemController>}
     */
    controllers: new Map(),

    state: {
      COUNTRY: {
        current: 0,
        original: 0,
      },
      LOCALE: {
        current: 0,
        original: 0,
      },
      CURRENCY: {
        current: 0,
        original: 0,
      },
    },

    setState(mod, key, value) {
      this.state[mod][key] = value;
    },

    get(key) {
      return this.controllers.get(key);
    },

    add(key, controller) {
      this.controllers.set(key, controller);
    },

    close() {
      this.closeCurrencyConverterPlus();
      this.controllers.forEach((controller) => {
        controller.close();
      });
    },

    closeCurrencyConverterPlus() {
      if (this.opened) {
        this.opened = false;
        // 兼容关闭 Currency Converter Plus
        _.$$(".currency-converter-chooser").forEach((el) => {
          el.style.display = "none";
        });
      }
    },

    reset() {
      this.controllers.forEach((controller) => {
        controller.reset();
      });
    },
  };

  let updating = false;

  class CountrySelectorItemController {
    /**
     * @param {string} key
     * @param {HTMLDivElement} root
     */
    constructor(key, root) {
      this.key = key.toUpperCase();
      this.id = `CountrySelector-${this.key}`;

      /** @type {HTMLDivElement} */
      this.$root = root;
      /** @type {HTMLButtonElement} */
      this.$trigger = _.$(selectors.SELECT_TRIGGER, this.$root);
      /** @type {HTMLSpanElement} */
      this.$triggerText = _.$(selectors.SELECT_TRIGGER_TEXT, this.$trigger);
      /** @type {HTMLSpanElement} */
      this.$triggerFlag = _.$(selectors.SELECT_TRIGGER_FLAG, this.$trigger);
      /** @type {HTMLDivElement} */
      this.$content = _.getSiblingElement(this.$trigger, selectors.OPTIONS);
      /** @type {HTMLInputElement} */
      this.$input = _.getSiblingElement(this.$trigger, selectors.INPUT);

      this._init(key);
    }

    _init() {
      this.type = this.$root.getAttribute("data-type");
      this.currentName = this.$trigger.getAttribute("data-name");
      this.currentValue = this.$trigger.getAttribute("data-value");
      this.originalName = this.currentName;
      this.originalValue = this.currentValue;

      this._buildPopup();
      this._initOptions();
      this._updateSelected();
      this._bindEvents();
    }

    _bindEvents() {
      _.on(this.$trigger, "click", this._open.bind(this));
      _.on(document, "click", this.close.bind(this));
      _.$$(selectors.OPTION, this.$content).forEach((el) => {
        _.on(el, "click", this._select.bind(this));
      });
      _.$$(selectors.SELECT_DISMISS, this.$popup).forEach((el) => {
        _.on(el, "click", this.close.bind(this));
      });
      _.$$(selectors.OPTION, this.$popup).forEach((el) => {
        _.on(el, "click", this._select.bind(this));
      });
    }

    _buildPopup() {
      this.$popup = _.createElement("div");
      this.$popup.id = this.id;
      this.$popup.classList.add("vograce-popup");
      this.$popup.classList.add("vograce-country-selector-popup");
      this.$popup.setAttribute("data-visibility", "hidden");
      this.$popup.setAttribute("style", "z-index: 9999");
      this.$popup.innerHTML = `
        <div class="vograce-overlay vograce-popup__overlay" data-dismiss="select"></div>
        <div class="vograce-popup__content">
          <div class="vograce-popup__body" role="dialog">
            <button type="button" class="vograce-popup__close-button" data-dismiss="select">
              <svg class="iconfont" aria-hidden="true">
                <use xlink:href="#icon-close"></use>
              </svg>
            </button>
            ${this.$content.innerHTML}
          </div>
        </div>
      `;
      document.body.appendChild(this.$popup);
    }

    _initOptions() {
      /** @type {HTMLElement[]} @description 桌面 */
      this.$options1 = [..._.$$(selectors.OPTION, this.$content).values()];
      /** @type {HTMLElement[]} @description 移动 */
      this.$options2 = [..._.$$(selectors.OPTION, this.$popup).values()];
    }

    _open(e) {
      e.stopPropagation();
      if (!this._opened) {
        store.close();
        this._opened = true;
        this._updateSelected();
        const el = _.isMobile() ? this.$popup : this.$content;
        el.setAttribute("data-visibility", "visible");
      } else {
        this.close();
      }
    }

    close(e) {
      if (e) e.stopPropagation();
      if (!this._opened) return;
      this._opened = false;
      const el = _.isMobile() ? this.$popup : this.$content;
      el.setAttribute("data-visibility", "hidden");
    }

    _select(e) {
      e.stopPropagation();
      this.setValue(e.target.closest(selectors.OPTION));
      this.close();
    }

    reset() {
      const options = this.getOptions();
      const option = options[store.state[this.key]["original"]];
      this.setValue(option);
    }

    getOptions() {
      return _.isMobile() ? this.$options2 : this.$options1;
    }

    _getAnotherOptions() {
      return _.isMobile() ? this.$options1 : this.$options2;
    }

    _getSelectedIndex(options) {
      return options.findIndex((o) => o.classList.contains("is--selected"));
    }

    _updateSelected() {
      const options = this.getOptions();
      const anotherOptions = this._getAnotherOptions();
      const i = this._getSelectedIndex(options);
      store.setState(this.key, "current", i);
      store.setState(this.key, "original", i);
      this.$selected = options[i];
      this.$anotherSelected = anotherOptions[i];
    }

    /**
     * @param {HTMLElement} el
     */
    _removeSelectedClass(el) {
      el && el.classList.remove("is--selected");
    }

    /**
     * @param {HTMLElement} el
     */
    _addSelectedClass(el) {
      el && el.classList.add("is--selected");
    }

    /**
     * @param {HTMLElement} el
     */
    _getData(el, key) {
      return el.getAttribute(`data-${key}`);
    }

    _setAnotherSelected() {
      const anotherOptions = this._getAnotherOptions();
      const i = store.state[this.key]["current"];
      this.$anotherSelected = anotherOptions[i];
    }

    /**
     * 设置当前选中的元素，并更新相关数据和UI
     * @param {HTMLElement} el - 新选中的元素
     */
    setValue(el) {
      this._removeSelectedClass(this.$selected);
      this._removeSelectedClass(this.$anotherSelected);
      // 确保DOM更新后才进行下一步操作
      setTimeout(() => {
        // 更新选中项和其相关数据
        this.$selected = el;
        this._addSelectedClass(this.$selected);

        // 从选中项获取索引、名称和值
        store.setState(
          this.key,
          "current",
          +this._getData(this.$selected, "index")
        );
        this.currentName = this._getData(this.$selected, "name");
        this.currentValue = this._getData(this.$selected, "value");
        this._setAnotherSelected();
        this._addSelectedClass(this.$anotherSelected);

        // 更新输入框的值和触发器的文本
        this.$input.value = this.currentValue;
        this.$triggerText.innerText = this.currentName;

        // 如果类型是国家，更新旗帜图标
        if (this.type === "country") {
          this.$triggerFlag.className = `currency-flag currency-flag--${this.currentValue}`;
          // 如果正在更新，直接返回
          if (updating) return;
          // 根据选中的国家更新货币信息
          this._updateCurrencyByCountry();
          // 更新完成后重置更新状态
          setTimeout(() => {
            updating = false;
          }, 0);
        }

        // 如果类型是货币，更新国家信息
        if (this.type === "currency") {
          // 如果正在更新，直接返回
          if (updating) return;
          // 根据选中的货币更新国家信息
          this._updateCountryByCurrency();
          // 更新完成后重置更新状态
          setTimeout(() => {
            updating = false;
          }, 0);
        }
      }, 0);
    }

    /**
     * 根据国家更新货币
     *
     * 此方法旨在当国家发生变化时，更新与该国家相关的货币选项
     * 它通过随机选择一个与国家关联的货币，并在界面上反映这一变化
     */
    _updateCurrencyByCountry() {
      // 设置更新状态为true，以指示当前正在执行更新操作
      updating = true;

      // 获取货币控制器，以便后续操作货币选项
      const controller = store.get("currency");

      // 根据当前国家获取相关的货币列表，如果找不到，则默认为空数组
      const currentValue = this.currentValue.toUpperCase();
      const currencies = countryToCurrencies[currentValue] || [];

      // 当存在可关联的货币时，执行更新操作
      if (currencies.length > 0) {
        // 随机选择一个货币，以增加一些不可预测性
        const index = Math.floor(Math.random() * currencies.length);
        const currency = currencies[index];

        // 在控制器的选项中找到对应货币的选项
        const option = controller
          .getOptions()
          .find((el) => el.dataset.value === currency);

        // 设置控制器的值为找到的选项，从而在界面上更新货币
        controller.setValue(option);
      }
    }

    /**
     * 根据货币更新国家
     *
     * 此方法旨在根据当前货币值来更新国家控制器的值
     * 它首先确定是否应根据货币更改国家，然后随机选择一个国家，
     * 最后将国家控制器的值设置为所选国家
     */
    _updateCountryByCurrency() {
      // 设置更新标志以防止重复更新
      updating = true;

      // 获取国家控制器实例
      const controller = store.get("country");

      // 根据当前货币值获取对应的国家列表
      const countries = currencyToCountries[this.currentValue] || [];

      // 如果存在对应的国家列表且不为空
      if (countries.length > 0) {
        // 随机选择一个国家
        const index = Math.floor(Math.random() * countries.length);
        const country = countries[index];

        // 在控制器的选项中找到与所选国家匹配的选项
        const option = controller
          .getOptions()
          .find((el) => el.dataset.value === country.toLowerCase());

        // 将控制器的值设置为所选国家
        controller.setValue(option);
      }
    }
  }

  class CountrySelectorController {
    constructor() {
      /** @type {HTMLButtonElement} */
      this.$trigger = _.$(selectors.TRIGGER);
      /** @type {HTMLDivElement} */
      this.$target = _.$(this.$trigger.getAttribute("data-target"));
      /** @type {HTMLDivElement} */
      this.$content = _.$(selectors.CONTENT);

      _.$$(selectors.CONTROL, this.$target).forEach((el) => {
        const key = el.dataset.type;
        if (!key) return;
        const controller = new CountrySelectorItemController(key, el);
        store.add(key, controller);
      });

      this._init();
    }

    _init() {
      this._bindEvents();
    }

    _bindEvents() {
      // 打开事件
      _.$$(selectors.TRIGGER).forEach((el) => {
        _.on(el, "click", this._open.bind(this));
      });

      // 关闭事件
      _.$$(selectors.DISMISS).forEach((el) => {
        _.on(el, "click", this._close.bind(this));
      });

      _.on(this.$content, "click", this._closeItem.bind(this));

      _.on(document, "click", this._close.bind(this));

      _.on(window, "resize", this._resize.bind(this));
    }

    /**
     * @param {Event} e
     */
    _open(e) {
      e.stopPropagation();
      /** @type {EventTarget} */
      this.currentTarget = e.currentTarget;
      this._resize();
      this._opened = true;
      this.$target.setAttribute("data-visibility", "visible");
      document.body.setAttribute("style", "overflow: hidden");
    }

    /**
     * @param {Event} e
     */
    _close(e) {
      e.stopPropagation();
      if (this._opened) {
        this._opened = false;
        this.$target.setAttribute("data-visibility", "hidden");
        this.$content.setAttribute("style", "");
        document.body.setAttribute("style", "overflow: auto");
      }
    }

    /**
     * @param {Event} e
     */
    _closeItem(e) {
      e.stopPropagation();
      store.close();
    }

    _resize() {
      if (_.isMobile()) {
        this.$content.setAttribute("style", "");
      } else {
        if (
          this.currentTarget &&
          _.isFunction(this.currentTarget.getBoundingClientRect)
        ) {
          /** @type {DOMRect} */
          const rect = this.currentTarget.getBoundingClientRect();
          const bottom = rect.bottom;
          const right = rect.right;

          this.$content.style.top = `${bottom}px`;
          this.$content.style.left = `${right - 400}px`;
          this.$content.style.transform = "translate3d(0, 15px, 0)";
        }
      }
    }
  }

  _.ready(() => {
    new CountrySelectorController();

    let count = 0;
    function loopSetEvent() {
      const __trigger = _.$(".currency-converter-currency-button");
      if (__trigger) {
        _.on(__trigger, "click", (e) => {
          store.close();
          store.opened = true;
        });

        _.on(document, "click", () => {
          store.closeCurrencyConverterPlus();
        });
      } else {
        if (count < 10) {
          count++;
          setTimeout(loopSetEvent, 2000);
        }
      }
    }

    setTimeout(() => {
      loopSetEvent();
    }, 5000);
  });
});

/**
 * ======================================
 * ======= Search
 * ======================================
 */
window.VOGRACE.sandbox((_) => {
  const selectors = {
    TRIGGER: ".vograce-search[data-trigger='search']",
    INPUT: ".vograce-search__input",
    CLEAR_BUTTON: ".vograce-search__input-button[data-button='clear']",
    SEARCH_BUTTON: ".vograce-search__input-button[data-button='search']",
    POPUP: ".vograce-search__popup",
    POPUP_CONTENT: ".vograce-search__content",
    KEYWORDS_LIST: ".vograce-search__popular-list",
    KEYWORDS_ITEM: ".vograce-search__popular-item",
    PRODUCTS_LIST: ".vograce-search__discover-list",
    PRODUCTS_ITEM: ".vograce-search__discover-item",
  };

  const state = {
    count: 0,
    keywords: "",
  };

  class Trigger {
    /**
     * @constructor
     * @param {HTMLElement} trigger
     */
    constructor(trigger) {
      /** @type {HTMLElement} */
      this.$trigger = trigger;
      /** @type {HTMLInputElement} */
      this.$input = _.$(selectors.INPUT, this.$trigger);
      /** @type {HTMLButtonElement} */
      this.$clearBtn = _.$(selectors.CLEAR_BUTTON, this.$trigger);
      /** @type {HTMLButtonElement} */
      this.$searchBtn = _.$(selectors.SEARCH_BUTTON, this.$trigger);

      this._handlers = {
        onOpen: _.noop,
        onSearch: _.noop,
        onSync: _.noop,
      };
    }

    bindEvents(events) {
      // 绑定外部事件处理器
      this._handlers.onOpen = events.onOpen || _.noop;
      this._handlers.onSearch = events.onSearch || _.noop;
      this._handlers.onSync = events.onSync || _.noop;

      // 绑定内部事件处理器
      _.on(this.$trigger, "click", this._onOpen.bind(this));
      _.on(this.$input, "focus", this._onFocus.bind(this));
      _.on(this.$input, "input", this._onInput.bind(this));
      _.on(this.$input, "keydown", this._onKeyDown.bind(this));
      _.on(this.$clearBtn, "click", this._onClear.bind(this));
      _.on(this.$searchBtn, "click", this._onSearch.bind(this));
    }

    _onOpen(e) {
      e.stopPropagation();
      this._handlers.onOpen(e);
    }

    _onFocus() {
      if (!this.$input.value) return;

      this._showClearBtn();
    }

    _onInput(e) {
      if (this._sync) return;

      state.keywords = e.target.value;
      state.keywords ? this._showClearBtn() : this._hideClearBtn();
      this._handlers.onSync();
    }

    _onKeyDown(e) {
      if (e.key !== "Enter") return;

      this.$searchBtn.click();
    }

    _onClear() {
      state.keywords = "";
      this._handlers.onSync();
      this._hideClearBtn();
    }

    syncInput() {
      if (state.keywords !== this.$input.value) {
        this._sync = true;
        this.$input.value = state.keywords;
        this._sync = false;
      }
    }

    _onSearch() {
      if (!state.keywords) return;

      this._handlers.onSearch();
    }

    _showClearBtn() {
      this.$clearBtn.setAttribute("data-visibility", "visible");
    }

    _hideClearBtn() {
      this.$clearBtn.setAttribute("data-visibility", "hidden");
    }
  }

  class Search {
    /**
     * @type {Trigger[]}
     */
    _triggers = [];

    constructor() {
      /** @type {HTMLElement} */
      this.$popup = _.$(selectors.POPUP);
      /** @type {HTMLElement} */
      this.$content = _.$(selectors.POPUP_CONTENT, this.$popup);

      this._debouncedOnSearch = _.debounce(this._onSearch.bind(this), 300);

      _.$$(selectors.TRIGGER).forEach((trigger) => {
        this._triggers.push(new Trigger(trigger));
      });

      this._fetchKeywordsAndProducts().then(() => {
        this._bindEvents();

        if (_.isMobile()) {
          this._createCloseButton();
        }
      });
    }

    async _fetchKeywordsAndProducts() {
      try {
        const response = await _.fetch.getJSON("/getTopKeywords");
        const { data } = await response.json();

        const keywords = this._transformKeywords(data.keywords);
        const products = this._transformProducts(data.products);

        const $keywords = _.$(selectors.KEYWORDS_LIST, this.$content);
        const $products = _.$(selectors.PRODUCTS_LIST, this.$content);

        $keywords.innerHTML = this._buildKeywords(keywords);
        $products.innerHTML = this._buildProducts(products);

        this.$content.setAttribute("data-visibility", "visible");
        this._cache = this.$content.innerHTML;
      } catch (error) {
        _.logger.error(error);
      }
    }

    _transformKeywords(keywords) {
      return keywords.map((keyword) => keyword.keyword);
    }

    _transformProducts(products) {
      return products.map((product) => ({
        name: product.title,
        imageUrl: product.url,
        url: product.onlineStoreUrl || product.onlineStorePreviewUrl,
        price: product.price,
      }));
    }

    _buildKeywords(keywords) {
      return keywords
        .map((keyword) => {
          return `<li class="vograce-search__popular-item">${keyword}</li>`;
        })
        .join("");
    }

    _buildProducts(products) {
      return products
        .map((product) => {
          return `
            <li class="vograce-search__discover-item" data-url="${product.url}">
              <img class="vograce-search__discover-img" src="${product.imageUrl}" alt="${product.name}">
              <div class="vograce-search__discover-meta">
                <p class="vograce-search__discover-name">${product.name}</p>
                <p class="vograce-search__discover-price">$${product.price}</p>
              </div>
            </li>
          `;
        })
        .join("");
    }

    _sendKeyword() {
      _.fetch.postJSON("/collectSearchKeywords", { keyword: state.keywords });
    }

    async _onSearch() {
      if (!state.keywords) return;

      state.count++;

      const value = state.keywords;
      const params = new URLSearchParams();
      params.append("q", value);
      params.append("resources[limit]", 3);
      params.append("resources[limit_scope]", "each");
      params.append("resources[options][unavailable_products]", "last");
      params.append("section_id", "search-results");

      _.showLoading(this.$content);

      try {
        const url = `/search/suggest?${params.toString()}`;
        const response = await _.fetch.get(url);

        await _.wait(1000);

        if (!response.ok) {
          throw new Error(response.status);
        }

        const html = await _.fetch.html(response);
        const element = _.$("#shopify-section-search-results", html);

        this._showResult(element);
      } catch (error) {
        this._hideResult();

        throw error;
      }
    }

    _bindEvents() {
      _.on(document, "click", this._onClosePopup.bind(this));
      _.on(this.$popup, "click", this._onClickPopup.bind(this));

      this._triggers.forEach((trigger) => {
        trigger.bindEvents({
          onOpen: this._onOpenPopup.bind(this),
          onSearch: this._navigateToSearchPage.bind(this),
          onSync: this._onSync.bind(this),
        });
      });
    }

    _onSync() {
      if (!state.keywords) {
        this._hideResult();
      }

      this._debouncedOnSearch();
      this._triggers.forEach((trigger) => {
        trigger.syncInput();
      });
    }

    _onClickPopup(e) {
      e.stopPropagation();

      if (e.target.closest(".results__search-btn")) {
        this._navigateToSearchPage();
        return;
      }

      const keyword = e.target.closest(selectors.KEYWORDS_ITEM);
      if (keyword) {
        state.keywords = keyword.textContent;
        this._onSync();
        this._debouncedOnSearch();
        return;
      }

      const product = e.target.closest(selectors.PRODUCTS_ITEM);
      if (product) {
        window.location.href = product.getAttribute("data-url");
      }
    }

    _createCloseButton() {
      const $header = _.$(".header-sticky-wrapper");
      const $logoWrapper = _.$(".header-toolbar-logo", $header);

      this.$logo = _.$(".site-header__logo", $logoWrapper);
      this.$logo.setAttribute("data-visibility", "visible");

      this.$closeBtn = _.createElement("button");
      this.$closeBtn.className = "vograce-search__close-button";
      this.$closeBtn.setAttribute("data-visibility", "hidden");
      this.$closeBtn.innerHTML = `
        <svg viewBox="64 64 896 896" focusable="false" data-icon="left" width="1em" height="1em" fill="currentColor" aria-hidden="true">
          <path d="M724 218.3V141c0-6.7-7.7-10.4-12.9-6.3L260.3 486.8a31.86 31.86 0 000 50.3l450.8 352.1c5.3 4.1 12.9.4 12.9-6.3v-77.3c0-4.9-2.3-9.6-6.1-12.6l-360-281 360-281.1c3.8-3 6.1-7.7 6.1-12.6z"></path>
        </svg>
        <span>Vograce</span>
      `;

      $logoWrapper.appendChild(this.$closeBtn);

      _.on($header, "click", (e) => {
        if (e.target.closest(".vograce-search__close-button") && this._opened) {
          this._hideCloseButtonOnMobile(e);
        }
      });
    }

    _showCloseButtonOnMobile() {
      this.$logo.setAttribute("data-visibility", "hidden");
      this.$closeBtn.setAttribute("data-visibility", "visible");
    }

    _hideCloseButtonOnMobile(e) {
      e.stopPropagation();
      this.$logo.setAttribute("data-visibility", "visible");
      this.$closeBtn.setAttribute("data-visibility", "hidden");
      this._closePopup();
    }

    _onOpenPopup(e) {
      if (this._opened) return;

      if (_.isMobile()) {
        document.body.setAttribute("style", "overflow: hidden");
        this._setPopupPositionOnMobile(e);
        this._showCloseButtonOnMobile();
      } else {
        this._setPopupPosition(e);
      }

      this._openPopup();
    }

    _onClosePopup() {
      if (!this._opened) return;

      this._closePopup();
    }

    _setPopupPositionOnMobile() {
      const $header = _.$("#HeaderWrapper");
      const rect = $header.getBoundingClientRect();

      this.$popup.style.top = `${rect.bottom}px`;
      this.$popup.style.bottom = 0;
      this.$popup.style.left = `${rect.left}px`;
      this.$popup.style.right = 0;
    }

    _setPopupPosition(e) {
      const target = e.currentTarget;
      const rect = target.getBoundingClientRect();

      this.$popup.style.top = `${rect.bottom + 20}px`;
      this.$popup.style.left = `${rect.left}px`;
    }

    _openPopup() {
      this._opened = true;
      this.$popup.setAttribute("data-visibility", "visible");
    }

    _closePopup() {
      this._opened = false;
      this.$popup.setAttribute("data-visibility", "hidden");
    }

    _navigateToSearchPage() {
      this._sendKeyword();
      window.location.href = `/search?q=${state.keywords}&options[prefix]=last`;
    }

    _showResult(element) {
      let show = true;

      if (state.count > 1) {
        state.count--;
        show = false;
      }

      if (state.count === 0) {
        show = false;
      }

      if (!state.keywords) {
        show = false;
      }

      if (!show) return;

      this.$content.innerHTML = element.innerHTML;
      state.count = 0;
    }

    _hideResult() {
      this.$content.innerHTML = this._cache;
      state.count = 0;
    }
  }

  _.ready(() => {
    new Search();
  });
});

/**
 * ======================================
 * ======= Tabs
 * ======================================
 */
window.VOGRACE.sandbox((_) => {
  const selectors = {
    ROOT: ".vograce-tabs",
    LIST: ".vograce-tabs__list",
    ITEM: ".vograce-tabs__item",
    CONTENT: ".vograce-tabs__content",
    PANE: ".vograce-tabs__pane",
  };

  class TabsController {
    /**
     * @constructor
     * @param {HTMLElement} root
     */
    constructor(root) {
      /** @type {HTMLElement} */
      this.$root = root;
      /** @type {HTMLElement} */
      this.$list = _.$(selectors.LIST, root);

      this._init();
    }

    _init() {
      _.on(this.$list, "click", this._select.bind(this));
    }

    /**
     * @param {Event} e
     */
    _select(e) {
      e.stopPropagation();
      const target = e.target.closest(selectors.ITEM);
      if (!target) return;

      // 移除所有选中状态
      _.$$(selectors.ITEM, this.$list).forEach((item) => {
        item.classList.remove("is--active");
      });

      // 添加选中状态
      target.classList.add("is--active");

      // 获取对应的内容区域
      const contentId = target.getAttribute("data-target");
      const $content = _.$(selectors.CONTENT, this.$root);
      if ($content) {
        // 隐藏所有内容
        _.$$(selectors.PANE, $content).forEach((pane) => {
          pane.setAttribute("data-visibility", "hidden");
        });
        // 显示当前选中的内容
        const $activePane = _.$(`#${contentId}`, $content);
        if ($activePane) {
          $activePane.setAttribute("data-visibility", "visible");
        }
      }
    }
  }

  _.ready(() => {
    _.$$(selectors.ROOT).forEach((root) => {
      new TabsController(root);
    });
  });
});

/**
 * ======================================
 * ======= Slider
 * ======================================
 */
window.VOGRACE.sandbox((_) => {
  const selectors = {
    ROOT: ".vograce-slider",
    SLIDE: ".vograce-slider__slide",
    LEFT_CONTROL: ".vograce-slider__arrow.is--left",
    RIGHT_CONTROL: ".vograce-slider__arrow.is--right",
  };

  class SliderController {
    constructor(root) {
      this._init(root);
    }

    _init(root) {
      /** @type {HTMLDivElement} */
      this.$root = root;
      /** @type {HTMLDivElement} */
      this.$slide = _.$(selectors.SLIDE, this.$root);
      /** @type {HTMLButtonElement} */
      this.$leftControl = _.$(selectors.LEFT_CONTROL, this.$root);
      /** @type {HTMLButtonElement} */
      this.$rightControl = _.$(selectors.RIGHT_CONTROL, this.$root);
      /** @type {Element} */
      this.$item = this.$slide.children[0];

      this._distance = 0;
      this._slideDistance = this._calculateSlideDistance();
      this._index = 0;
      this._maxIndex = this._calculateMaxIndex();
      this._numberOfRollingSlides = this._getNumberOfRollingSlides();

      this._toggleControlsDisabled();
      this._bindEvents();
    }

    _getNumberOfRollingSlides() {
      return Number(this.$root.getAttribute("data-rolling-slides"));
    }

    _toggleControlsDisabled() {
      if (this._index === 0) {
        this.$leftControl.setAttribute("disabled", "disabled");
      } else {
        this.$leftControl.removeAttribute("disabled");
      }

      if (this._index === this._maxIndex) {
        this.$rightControl.setAttribute("disabled", "disabled");
      } else {
        this.$rightControl.removeAttribute("disabled");
      }
    }

    _calculateMaxIndex() {
      const containerWidth = this.$slide.getBoundingClientRect().width;
      const containerWidthWithGap = containerWidth + 24;
      const itemWidth = this.$item.getBoundingClientRect().width;
      const itemWidthWithGap = itemWidth + 24;
      const numberOfItems = containerWidthWithGap / itemWidthWithGap;
      const different = this.$slide.children.length - numberOfItems;
      return different > 0 ? different : 0;
    }

    _calculateSlideDistance() {
      return this.$item.getBoundingClientRect().width + 24;
    }

    _bindEvents() {
      _.on(this.$leftControl, "click", this._scrollLeft.bind(this));
      _.on(this.$rightControl, "click", this._scrollRight.bind(this));
    }

    _calculateDistance() {
      return this._numberOfRollingSlides * this._slideDistance;
    }

    _scrollLeft() {
      if (this._index === 0) {
        return;
      }

      this._distance -= this._calculateDistance();
      this._index -= this._numberOfRollingSlides;
      this._scroll();
    }

    _scrollRight() {
      if (this._index === this._maxIndex) {
        return;
      }

      this._distance += this._calculateDistance();
      this._index += this._numberOfRollingSlides;
      this._scroll();
    }

    _scroll() {
      this._toggleControlsDisabled();
      this.$slide.style.transform = `translateX(-${this._distance}px)`;
    }
  }

  _.ready(() => {
    const controllers = {};

    _.$$(selectors.ROOT).forEach((el) => {
      const id = el.parentElement.id;
      const controller = new SliderController(el);
      controllers[id] = controller;
    });
  });
});

/**
 * ======================================
 * ======= Brand Equity
 * ======================================
 */
window.VOGRACE.sandbox((_) => {
  const selectors = {
    ROOT: ".vograce-brand-equity--desktop",
    WRAPPER: ".vograce-brand-equity__top",
    INNER: ".vograce-brand-equity__top-inner",
  };

  class BrandEquityController {
    constructor() {
      if (_.isMobile()) return;
      this._init();
    }

    _init() {
      /** @type {Element} */
      this.$root = _.$(selectors.ROOT);
      /** @type {Element} */
      this.$inner = _.$(selectors.INNER, this.$wrapper);

      this._maxWidth = window.innerWidth;
      this._maxHeight = window.innerHeight;

      const $section = this.$root.parentElement.parentElement;
      const $container = this.$root.parentElement;
      const $wrapper = _.$(selectors.WRAPPER, this.$root);

      $section.prepend(
        _.createElement("div", {
          style: `width: ${this._maxWidth}px; height: ${this._maxHeight}px;`,
        })
      );

      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const r1 = $section.getBoundingClientRect();
      const r2 = $container.getBoundingClientRect();
      const r3 = $wrapper.getBoundingClientRect();

      this._minWidth = r3.width;
      this._minHeight = r3.height;
      this._thresholdTop = scrollTop + r1.top;
      this._thresholdBottom = scrollTop + r2.top - Math.floor(r2.height / 1.5);
      this._scrollHeight = this._thresholdBottom - this._thresholdTop;
      this._top = this._thresholdTop - (scrollTop + r3.top);

      this.$inner.style.top = `${this._top}px`;
      this.$inner.style.width = `${this._maxWidth}px`;
      this.$inner.style.height = `${this._maxHeight}px`;

      // 分别计算最小宽度和最大宽度以及最小高度和最大高度的差值
      this._widthDiff = this._maxWidth - this._minWidth;
      this._heightDiff = this._maxHeight - this._minHeight;

      // 计算阈值底部到顶部的距离
      this._thresholdDistance = this._thresholdBottom - this._thresholdTop;

      this._observe();
    }

    _observe() {
      // 使用requestAnimationFrame实现更精确的节流
      let frameId = null;
      const handleScrollRAF = () => {
        if (frameId) cancelAnimationFrame(frameId);
        frameId = requestAnimationFrame(this._handleScroll.bind(this));
      };

      // 使用箭头函数保存this上下文
      this._throttledScroll = handleScrollRAF;

      _.on(window, "resize", this._throttledScroll);
      _.on(window, "scroll", this._throttledScroll);
    }

    // 优化后的滚动处理函数
    _handleScroll() {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      let styleConfig;

      // 检查是否在阈值范围内
      if (scrollTop > this._thresholdTop && scrollTop < this._thresholdBottom) {
        // 计算滚动比例，保持在0-1范围内
        const ratio = Math.min(
          1,
          Math.max(
            0,
            (scrollTop - this._thresholdTop) / this._thresholdDistance
          )
        );

        // 使用线性插值计算样式值
        styleConfig = {
          top: this._interpolate(this._top, 0, ratio),
          width: this._interpolate(this._maxWidth, this._minWidth, ratio),
          height: this._interpolate(this._maxHeight, this._minHeight, ratio),
          borderRadius: "",
        };
      }
      // 滚动位置在阈值之上
      else if (scrollTop <= this._thresholdTop) {
        styleConfig = {
          top: this._top,
          width: this._maxWidth,
          height: this._maxHeight,
          borderRadius: "",
        };
      }
      // 滚动位置在阈值之下
      else {
        styleConfig = {
          top: 0,
          width: this._minWidth,
          height: this._minHeight,
          borderRadius: "var(--vograce-spacing-md)",
        };
      }

      // 应用样式
      this._applyStyle(styleConfig);
    }

    // 辅助方法：线性插值计算
    _interpolate(start, end, ratio) {
      return start + (end - start) * ratio;
    }

    // 辅助方法：应用样式
    _applyStyle(config) {
      const { top, width, height, borderRadius } = config;
      this.$inner.style.cssText = `
        top: ${top}px;
        width: ${width}px;
        height: ${height}px;
        border-radius: ${borderRadius};
      `;
    }
  }

  _.ready(() => {
    new BrandEquityController();
  });
});

/**
 * ======================================
 * ======= Member
 * ======================================
 */

window.VOGRACE.sandbox((_) => {
  const selectors = {
    ROOT: ".vograce-member",
    TITLE: ".vograce-member__title",
    POINTS: ".vograce-member__points",
  };

  const levels = {
    regular_member: "is--regular",
    silver_member: "is--silver",
    gold_member: "is--gold",
    black_member: "is--black",
  };

  class MemberController {
    constructor() {
      /** @type {HTMLElement} */
      this.$root = _.$(selectors.ROOT);
      /** @type {HTMLElement} */
      this.$title = _.$(selectors.TITLE, this.$root);
      /** @type {HTMLElement} */
      this.$points = _.$(selectors.POINTS, this.$root);

      this._init();
    }

    async _init() {
      const { data } = await this._fetchData();
      if (Object.keys(data).length === 0) return;
      const level = levels[data.customer_level];
      this.$root.classList.add(level);
      this.$title.innerHTML = `Hi, ${data.email}`;
      this.$points.innerHTML = data.points;
    }

    async _fetchData() {
      const response = await _.fetch.getJSON("/account/currentAccountInfo", {
        headers: {
          "customer-id": customer_id,
          sign: customer_slat,
          timestamp: now_timestamp,
        },
      });
      return await response.json();
    }
  }

  _.ready(() => {
    if (!customer_id) return;

    new MemberController();
  });
});

/**
 * ======================================
 * ======= Statistic
 * ======================================
 */

window.VOGRACE.sandbox((_) => {
  const selectors = {
    CAROUSEL: ".vograce-statistic__carousel-inner",
    ITEM: ".vograce-statistic__item",
  };

  class StatisticController {
    constructor() {
      /** @type {HTMLElement} */
      this.$carousel = _.$(selectors.CAROUSEL);
      /** @type {HTMLElement[]} */
      this._items = _.$$(selectors.ITEM);
      this._current = 0;
      this._distance = 0;
      this._interval = 2000;
      this._itemWidth = 0;
      this._totalItems = this._items.length;
      this._isTransitioning = false;
      this._rafId = null;
      this._lastTime = 0;

      // 克隆首尾元素
      this._cloneFirstAndLastItem();

      this._init();
    }

    _cloneFirstAndLastItem() {
      if (this._totalItems < 1) return;
      const firstItem = this._items[0].cloneNode(true);
      const lastItem = this._items[this._items.length - 1].cloneNode(true);
      this.$carousel.appendChild(firstItem);
      this.$carousel.insertBefore(lastItem, this._items[0]);
      this._items = Array.from(this.$carousel.children);
      this._totalItems = this._items.length;
    }

    _init() {
      if (this._totalItems < 5) return;

      this._current = 1;
      this._distance = this._calculateDistanceToIndex(this._current);
      this.$carousel.style.transform = `translateX(-${this._distance}px)`;

      this._lastTime = performance.now();
      this._animate(performance.now());

      // 监听窗口变化，重新计算布局
      _.on(window, "resize", this._handleResize.bind(this));
    }

    _handleResize() {
      this._distance = this._calculateDistanceToIndex(this._current);
      this.$carousel.style.transform = `translateX(-${this._distance}px)`;
    }

    _calculateDistanceToIndex(index) {
      let distance = 0;
      for (let i = 0; i < index; i++) {
        distance += this._items[i]?.offsetWidth || 0;
      }
      return distance;
    }

    _isLastItemVisible(offsetThreshold = 100) {
      const containerWidth = this.$carousel.offsetWidth;
      const scrollPosition = this._distance;
      const lastItem = this._items[this._totalItems - 2]; // 倒数第二个元素为真实最后一个元素
      const itemOffsetLeft = lastItem.offsetLeft;
      const itemWidth = lastItem.offsetWidth;

      // 增加偏移量，提前出发重置
      return (
        itemOffsetLeft + itemWidth <=
        scrollPosition + containerWidth + offsetThreshold
      );
    }

    _scrollToIndex(index) {
      this._isTransitioning = true;
      this._current = index;
      this._distance = this._calculateDistanceToIndex(index);

      if (this._isLastItemVisible()) {
        this._current = 1;
        this._distance = this._calculateDistanceToIndex(this._current);
        this.$carousel.style.transition = "none";
        this.$carousel.style.transform = `translateX(-${this._distance}px)`;

        // 直接释放锁并调用 _animate
        this._isTransitioning = false;
        this._animate(performance.now());
      } else {
        this.$carousel.style.transition = "transform 1.2s ease-in-out";
        this.$carousel.style.transform = `translateX(-${this._distance}px)`;

        // 先移除旧的 transitionend 监听器
        _.off(this.$carousel, "transitionend", this._boundTransitionEnd);

        // 创建一个绑定 this 的过渡结束回调
        this._boundTransitionEnd = () => {
          if (this._current === 1) {
            // 恢复过渡效果
            this.$carousel.style.transition = "transform 1.2s ease-in-out";
          }

          this._isTransitioning = false;
          this._animate(performance.now());

          // 移除当前监听器，防止重复触发
          _.off(this.$carousel, "transitionend", this._boundTransitionEnd);
        };

        _.on(this.$carousel, "transitionend", this._boundTransitionEnd);
      }
    }

    _next() {
      if (this._isTransitioning) return;

      const nextIndex = this._current + 1;

      if (nextIndex >= this._totalItems) {
        this._scrollToIndex(1); // 回到第一个
      } else {
        this._scrollToIndex(nextIndex);
      }
    }

    _animate(now) {
      if (this._isTransitioning) return;

      const delta = now - this._lastTime;

      if (delta >= this._interval) {
        this._lastTime = now;
        this._next();
      } else {
        this._rafId = window.requestAnimationFrame(this._animate.bind(this));
      }
    }

    _destroy() {
      if (this._rafId) {
        window.cancelAnimationFrame(this._rafId);
      }
    }
  }

  _.ready(() => {
    new StatisticController();
  });
});

/**
 * ======================================
 * ======= Accordion
 * ======================================
 */

window.VOGRACE.sandbox((_) => {
  const selectors = {
    ROOT: "[data-accordion]",
    TRIGGER: "[data-trigger='accordion']",
    CONTENT: ".vograce-events__content",
    EXTRA_IMAGE: ".vograce-events__extra .vograce-events__image-wrapper",
  };

  class AccordionController {
    constructor() {
      /** @type {Element} */
      this.$root = _.$("[data-accordion]");

      this._init();
    }

    _init() {
      this._bindEvents();
      this._openFirstItem();
    }

    _bindEvents() {
      _.on(this.$root, "click", (e) => {
        const target = e.target.closest(selectors.TRIGGER);
        if (!target) return;

        e.preventDefault();
        this._updateActive(target);
        this._updateContent(target);
        this._updateCover(target);
      });
    }

    _updateActive(target) {
      // 关闭所有已展开项
      _.$$(selectors.TRIGGER).forEach((el) => {
        el.classList.remove("is--active");
      });

      target.classList.add("is--active");
    }

    _updateContent(target) {
      const id = target.getAttribute("data-target");
      const element = _.$(id);

      // 关闭所有已展开项
      _.$$(selectors.CONTENT).forEach((el) => {
        el.setAttribute("data-visibility", "hidden");
      });

      // 如果当前项是隐藏状态，则展开
      if (element.getAttribute("data-visibility") !== "visible") {
        element.setAttribute("data-visibility", "visible");
      }
    }

    _updateCover(target) {
      const id = target.getAttribute("data-cover");
      const element = _.$(id);

      // 关闭所有已展开项
      _.$$(selectors.EXTRA_IMAGE).forEach((el) => {
        el.setAttribute("data-visibility", "hidden");
      });

      // 如果当前项是隐藏状态，则展开
      if (element.getAttribute("data-visibility") !== "visible") {
        element.setAttribute("data-visibility", "visible");
      }
    }

    _openFirstItem() {
      const firstTrigger = _.$(selectors.TRIGGER);
      const firstContent = _.$(firstTrigger.getAttribute("data-target"));
      if (firstTrigger && firstContent) {
        firstContent.setAttribute("data-visibility", "visible");
        firstTrigger.classList.add("is--active");
      }
    }
  }

  _.ready(() => {
    new AccordionController();
  });
});

/**
 * ======================================
 * ======= Look
 * ======================================
 */

window.VOGRACE.sandbox((_) => {
  const selectors = {
    ROOT: ".vograce-look",
    HOTSPOTS: ".vograce-look__hotspots",
    HOTSPOT: ".vograce-look__hotspot",
  };

  class LookController {
    /**
     * @constructor
     * @param {HTMLElement} root
     */
    constructor(root) {
      /** @type {HTMLElement} */
      this.$root = root;
      /** @type {HTMLElement} */
      this.$hotspots = _.$(selectors.HOTSPOTS, this.$root);

      this._init();
    }

    _init() {
      _.on(this.$hotspots, "click", this._onScrollIntoView.bind(this));
    }

    _onScrollIntoView(e) {
      const hotspot = e.target.closest(selectors.HOTSPOT);

      if (hotspot) {
        const target = hotspot.dataset.target;
        const element = _.$(target);

        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }
    }
  }

  _.ready(() => {
    _.$$(selectors.ROOT).forEach((root) => {
      new LookController(root);
    });
  });
});

/**
 * ======================================
 * ======= Product - Add to Cart
 * ======================================
 */

window.VOGRACE.sandbox((_) => {
  const selector = {
    ADD_TO_CART: "[data-trigger='add-to-cart']",
  };

  /**
   * 添加到购物车
   * @param {Event} e
   */
  async function addToCart(e) {
    e.preventDefault();
    e.stopPropagation();

    const target = e.currentTarget;
    const backup = target.innerHTML;
    const sectionId = target.getAttribute("data-section-id");
    const productId = target.getAttribute("data-product-id");
    const variantId = target.getAttribute("data-variant-id");

    _.showLoading(target);

    const params = new URLSearchParams();
    params.append("form_type", "product");
    params.append("utf8", "✓");
    params.append("section-id", sectionId);
    params.append("product-id", productId);
    params.append("id", variantId);

    try {
      await _.fetch.post("/cart/add", params.toString(), {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      VogUtils.showMessage("Add to cart successfully", "success");
    } catch (error) {
      _.logger.error(error);
      VogUtils.showMessage("Add to cart failed", "error");
    } finally {
      target.innerHTML = backup;
    }
  }

  _.ready(() => {
    _.$$(selector.ADD_TO_CART).forEach((el) => {
      _.on(el, "click", addToCart);
    });
  });
});
